import { Badge, Flex, Mark, Tooltip } from "@mantine/core";
import * as _ from "lodash-es";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_TableOptions,
  useMantineReactTable
} from "mantine-react-table-open";
import { useState } from "react";
import {
  generatePath,
  Link,
  Route,
  Routes,
  useNavigate,
  useParams
} from "react-router";
import { getDefaultMRTOptions } from "../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../mantine/getDefaultMRTRowEditModeOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { useToast } from "../../../mantine/notifications/useToast";
import { ROUTES } from "../../../routes";
import type { Person } from "../../../utils/backend-types";
import { ObjectName } from "../../../utils/enums";
import { MultiConfirmationModal } from "../../BuildingBlocks/Layout/Modals/MultiConfirmationModal/MultiConfirmationModal";
import { getCompanyFeatures } from "../../ComponentListContainer/ComponentList/ComponentEditWizard/Data/utils/getCompanyFeatures";
import { usePersonMutations } from "../../PersonWizard/hooks/usePersonMutations";
import { PersonWizard } from "../../PersonWizard/PersonWizard";
import { ProjectViewTabs } from "../MsmProjectView";
import { getSitesWhereActive } from "./columns/getSitesWhereActive";
import "./CompaniesPage.scss";

interface CompaniesPageProps {
  selectedCompanyId?: number;
  companies: Array<Person>;
  variantId: number;
  showNewModal?: boolean;
}

function CompaniesPage({
  selectedCompanyId,
  companies,
  variantId,
  showNewModal = false
}: CompaniesPageProps) {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const companiesPath = `${generatePath(ROUTES.project.entry, {
    projectId: projectId ?? ""
  })}/${ProjectViewTabs.Companies}`;

  function toggleCreateModal() {
    if (showNewModal) {
      navigate(companiesPath, { replace: true });
    } else {
      navigate("neu");
    }
  }

  function handleCloseEditModal() {
    navigate(companiesPath);
  }

  return (
    <div className="CompaniesPage">
      <CompaniesList companies={companies} variantId={variantId} />
      {showNewModal && (
        <PersonWizard
          isOpen
          mode="create"
          variantId={variantId}
          onClose={toggleCreateModal}
        />
      )}
      {selectedCompanyId && (
        <PersonWizard
          isOpen
          mode="edit"
          objectId={selectedCompanyId}
          variantId={variantId}
          onClose={handleCloseEditModal}
        />
      )}
    </div>
  );
}

interface CompaniesListProps {
  companies: Array<Person>;
  variantId: number;
}

function CompaniesList({ companies, variantId }: CompaniesListProps) {
  const { showToast, showErrorToast } = useToast();
  const [companiesForDeletion, setCompaniesForDeletion] = useState<
    Array<Person>
  >([]);
  const navigate = useNavigate();

  const { deletePersonMutation } = usePersonMutations({
    variantId: variantId
  });

  async function deleteCompanies() {
    const companyIds = companiesForDeletion.map((c) => c.id);
    const allPromises = companyIds.map((id) =>
      deletePersonMutation.mutateAsync(id)
    );
    return Promise.all(allPromises)
      .then(() => {
        const companyNames = companiesForDeletion.map((c) => c.name);
        showToast({
          type: "success",
          message: `Die Unternehmen ${companyNames.join(", ")} wurden erfolgreich gelöscht.`
        });
        setCompaniesForDeletion([]);
      })
      .catch((error) => {
        showErrorToast(error);
      });
  }

  const cols: Array<MRT_ColumnDef<Person>> = [
    {
      header: "Name",
      accessorKey: "name",
      Cell: ({ row }) => (
        <Link to={`./${row.original.id}`}>{row.original.name}</Link>
      ),
      sortingFn: "alphanumeric",
      size: 350
    },
    getSitesWhereActive(companies),
    {
      header: "Gebuchte Add-Ons",
      accessorFn: (row) =>
        getCompanyFeatures(row)
          .map((f) => f.label)
          .join(", "),
      size: 300,
      Cell: ({ row }) => (
        <Flex gap="xs">
          {getCompanyFeatures(row.original).map((feature) => {
            const Icon = feature.icon;
            return (
              <Tooltip key={feature.key} label={feature.label}>
                <Badge color="gray" leftSection={<Icon size={16} />}>
                  {feature.label}
                </Badge>
              </Tooltip>
            );
          })}
        </Flex>
      ),
      id: "features"
    }
  ];

  const defaultOptions: Partial<MRT_TableOptions<Person>> =
    getDefaultMRTOptions({ hideBorder: true });
  const topActionsOptions: Partial<MRT_TableOptions<Person>> =
    getMRTTopToolbarCustomActionsRenderer({
      create: {
        text: "Unternehmen erstellen",
        onClick: () => navigate("./neu")
      },
      delete: {
        text: "Unternehmen löschen",
        onClick: (rowSelection) => {
          setCompaniesForDeletion(
            Object.keys(rowSelection).map((id) =>
              companies.find((c) => c.id === parseInt(id, 10))
            ) as Array<Person>
          );
        }
      }
    });
  const rowActionsOptions: Partial<MRT_TableOptions<Person>> =
    getDefaultMRTRowEditModeOptions({
      rowActions: {
        edit: {
          text: "Unternehmen bearbeiten",
          onClick: (row) => {
            navigate(`${row.original.id}/`);
            table.setEditingRow(null);
          }
        },
        delete: {
          text: "Unternehmen löschen",
          onClick: (row) => setCompaniesForDeletion([row.original])
        }
      }
    });
  const customOptions: Partial<MRT_TableOptions<Person>> = {
    initialState: { showGlobalFilter: true },
    enableGlobalFilter: true,
    globalFilterFn: "includesString",
    enablePagination: false,
    enableColumnResizing: true,
    enableColumnFilters: true,
    columnFilterDisplayMode: "popover",
    enableFacetedValues: true
  };
  const options = _.merge(
    {},
    defaultOptions,
    topActionsOptions,
    rowActionsOptions,
    customOptions
  );

  const table = useMantineReactTable<Person>({
    ...options,
    columns: cols,
    data: companies
  });

  return (
    <>
      <MantineReactTable table={table} />
      {companiesForDeletion.length > 0 && (
        <MultiConfirmationModal
          actionName="löschen"
          actionObjects={companiesForDeletion.map((c) => c.name)}
          confirmAction
          confirmationText="Möchten Sie die folgenden Unternehmen löschen? Dieser Schritt kann nicht rückgängig gemacht werden."
          isModalOpen
          matchTextToConfirmActionText={
            <span>
              Geben Sie zur Bestätigung den Namen des Unternehmens{" "}
              <Mark>{companiesForDeletion[0].name}</Mark> in das Textfeld ein.
            </span>
          }
          objectName={ObjectName.Person}
          toggleModal={() => setCompaniesForDeletion([])}
          onAction={deleteCompanies}
        />
      )}
    </>
  );
}

function CompaniesPageWithRoutes(props: CompaniesPageProps) {
  return (
    <Routes>
      <Route
        element={<CompaniesPageWithSelectedCompany {...props} />}
        path=":companyId"
      >
        <Route
          element={<CompaniesPageWithSelectedCompany {...props} />}
          index
        />
        <Route
          element={<CompaniesPageWithSelectedCompany {...props} />}
          path="*"
        />
      </Route>
      <Route element={<CompaniesPage {...props} showNewModal />} path="neu" />
      <Route element={<CompaniesPage {...props} />} index />
    </Routes>
  );
}

function CompaniesPageWithSelectedCompany(props: CompaniesPageProps) {
  const { companyId } = useParams();
  const parsedCompanyId = companyId ? parseInt(companyId, 10) : undefined;

  return <CompaniesPage selectedCompanyId={parsedCompanyId} {...props} />;
}

export { CompaniesPageWithRoutes as CompaniesPage, CompaniesPageProps };
